const _transformToPixels = (value: number | undefined): string =>
  value || value === 0 ? `${value}px` : '';

const _transformToNumber = (value: string | undefined): number =>
  value ? Number(value.replace('px', '')) : 0;

const _calculatePositionWithOffset = (
  value: number | undefined,
  offset: number,
  operation: 'add' | 'subtract' = 'add',
): number | undefined =>
  value || value === 0 ? (operation === 'add' ? value + offset : value - offset) : undefined;

/**
 * Verifica se um elemento HTML está acima de outro de referência. Por padrão a
 * referência é o elemento HTMl raiz, verificando se está acima da tela.
 *
 * @param target Elemento HTML que será verificado.
 * @param reference Elemento HTML de referência. Padrão elemento raíz (HTML).
 *
 * @category DOM
 */
export const isElementAboveReference = (
  target: HTMLElement,
  reference: HTMLElement = document.documentElement,
): boolean => {
  const { bottom } = target.getBoundingClientRect();
  const { top: referenceTop } = reference.getBoundingClientRect();

  return bottom < referenceTop;
};

/**
 * Verifica se um elemento HTML está abaixo de outro de referência. Por padrão a
 * referência é o elemento HTMl raiz, verificando se está abaixo da tela.
 *
 * @param target Elemento HTML que será verificado.
 * @param reference Elemento HTML de referência. Padrão elemento raíz (HTML).
 *
 * @category DOM
 */
export const isElementBelowReference = (
  target: HTMLElement,
  reference: HTMLElement = document.documentElement,
): boolean => {
  const { top } = target.getBoundingClientRect();
  const { bottom: referenceBottom } = reference.getBoundingClientRect();

  return top > referenceBottom;
};

/**
 * Verifica se a parte debaixo do componente está acima da parte debaixo de outro
 * elemento HTML de referência. Isso indica se eles estão sobrepostos.
 *
 * @param target Elemento HTML que será verificado.
 * @param reference Elemento HTML de referência.
 *
 * @category DOM
 */
export const isElementBottomAboveReferenceBottom = (
  target: HTMLElement,
  reference: HTMLElement = document.documentElement,
  difference = 0,
): boolean => {
  const { bottom } = target.getBoundingClientRect();
  const { bottom: referenceBottom } = reference.getBoundingClientRect();

  return bottom + difference < referenceBottom;
};

/**
 * Posiciona um elemento HTML sobre outro de referência ou remove as posições se
 * nenhuma referência for informada.
 *
 * @param target Elemento HTML que será reposicionado.
 * @param reference Elemento HTML que serão copiadas as posições. Se não enviado,
 * se assume que irá resetar as posições do elemento HTML alvo.
 */
export const placeOver = (target: HTMLElement, reference?: HTMLElement): void => {
  const { style } = target;
  const { top, right, bottom, left, width, height } = reference?.getBoundingClientRect?.() || {};
  const {
    font = '',
    paddingTop = '0',
    paddingBottom = '0',
  } = reference ? getComputedStyle(reference) : {};

  style.top = _transformToPixels(_calculatePositionWithOffset(top, _transformToNumber(paddingTop)));
  style.right = _transformToPixels(right);
  style.bottom = _transformToPixels(
    _calculatePositionWithOffset(bottom, _transformToNumber(paddingBottom), 'subtract'),
  );
  style.left = _transformToPixels(left);
  style.width = _transformToPixels(width);
  style.height = _transformToPixels(
    _calculatePositionWithOffset(
      height,
      _transformToNumber(paddingTop) + _transformToNumber(paddingBottom),
      'subtract',
    ),
  );
  style.position = reference ? 'fixed' : '';
  style.font = font;
};
